import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { Box, ContentTemplate, ErrorInfo, Progress, SectionHeader, Typography } from 'web-library';
import DetailsContent from '../../../modules/groupSpecialConsideration/components/DetailsContent';
import Declaration from '../../../components/Declaration';
import NavigationButtons from '../../../components/NavigationButtons';
import { useGetGroupSCReview } from '../../../api-refactored/group-special-consideration/useGetGroupSCReview';
import { usePutSubmitRequest } from '../../../api-refactored/group-special-consideration/usePutSubmitRequest';
import RouteKey from '../../../routes/key';
import { useGetRequestSummary } from '../../../api-refactored/group-special-consideration/useGetRequestSummary';
import DraftTimeStamp from '../../../components/DraftTimeStamp';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  marginBottom4: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
  },
  buttonDraftTimeContainer: {
    marginBottom: theme.spacing(7),
  },
}));

const {
  coordinatorUploadSupportingDocument,
  amendCoordinatorUploadSupportingDocument,
  coordinatorApplicationSubmitted,
} = RouteKey['special-consideration'].child;

interface ReviewProps {
  amendment: boolean;
}

const Review = ({ amendment }: ReviewProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [declErr, setDeclErr] = useState<boolean>(false);
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>();

  const { data: groupSCReview } = useGetGroupSCReview({
    uuid,
    onError: (err) => setErrMsg(err.response?.data.message),
  });

  const { data: requestSummary } = useGetRequestSummary({
    uuid,
    onError: (err) => setErrMsg(err.response?.data.message),
  });

  const { mutate: putSubmitRequest } = usePutSubmitRequest({
    uuid,
    onSuccess: () => history.replace(`${coordinatorApplicationSubmitted.route}`),
    onError: (err) => setErrMsg(err.response?.data.message),
  });

  if (errMsg) {
    return <ErrorInfo label={errMsg} />;
  }

  if (!groupSCReview || !requestSummary) {
    return <Progress color="secondary" />;
  }

  const handleAgreement = () => {
    setDeclErr(false);
    setIsAgreed(!isAgreed);
  };

  const handleBack = () => {
    if (amendment) {
      history.push(`${amendCoordinatorUploadSupportingDocument.route}`);
    } else {
      history.push(`${coordinatorUploadSupportingDocument.route}`);
    }
  };

  const handleSubmitApplication = () => {
    if (isAgreed) {
      putSubmitRequest();
    } else {
      setDeclErr(true);
    }
  };

  return (
    <>
      <ContentTemplate type="large" isContinuable>
        <SectionHeader
          title={t('specialConsideration->>ApplyForSpecialConsideration.ReviewApplication')}
        />
      </ContentTemplate>
      <DetailsContent groupSCReview={groupSCReview} amendment={amendment} />
      <ContentTemplate isDividerTop type="large">
        <Typography variant="h3" className={classes.marginBottom4}>
          {t('specialConsideration->>ApplyForSpecialConsideration.Declaration')}
        </Typography>
        <Declaration
          noTitle
          isAgreed={isAgreed}
          handleAgreement={handleAgreement}
          customContent={t(
            'specialConsideration->>ApplyForSpecialConsideration.CoordinatorDeclarationText'
          )}
        />
        {declErr && (
          <Box>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {t('specialConsideration->>ApplyForSpecialConsideration.DeclarationError')}
            </Typography>
          </Box>
        )}
        {errMsg && (
          <Box>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {errMsg}
            </Typography>
          </Box>
        )}
        <Box className={classes.buttonDraftTimeContainer}>
          <NavigationButtons
            backLabel={t('specialConsideration->>ApplyForSpecialConsideration.Back')}
            procceedLabel={t(
              'specialConsideration->>ApplyForSpecialConsideration.SubmitApplication'
            )}
            onBackClick={handleBack}
            onProceedClick={handleSubmitApplication}
          />
          <DraftTimeStamp savedAt={requestSummary.lastUpdatedTime} />
        </Box>
      </ContentTemplate>
    </>
  );
};

export default Review;
